import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  TextField,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Divider,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Autocomplete,
  Paper,
  Card,
  CardContent,
  Grid
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TitleIcon from "@mui/icons-material/Title";
import DescriptionIcon from "@mui/icons-material/Description";
import Pagination from "@mui/material/Pagination";
import { categoryService } from "../services/categoryService";
import {
  addBulkText,
  fetchBulkText,
} from "../redux/actions/bulkTextTransactionAction";
import useAlert from "../hooks/useAlert";

export const BatchTextProcesses = () => {
  // State'ler
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [text, setText] = useState("");
  const [replace, setReplace] = useState("");
  const [selectSearchTerm, setSelectSearchTerm] = useState("");
  const [type, setType] = useState(0);
  const [textType, setTextType] = useState(0); // 0: Başlık, 1: Açıklama
  const token = useSelector((state) => state?.auth?.user?.accessToken?.token);
  const [bulkTexts, setBulkTexts] = useState([]);
  const [totalBulk, setTotalBulk] = useState(0);
  // Sayfalama için page state'i
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const authUserId = useSelector((state) => state?.auth?.user?.id);
  const dispatch = useDispatch();

  const { showErrorAlertBulkPrice, showSuccessAlertBulkPrice } =
    useAlert();

  // Kategorileri çek
  const fetchCategories = async () => {
    try {
      const fetchedCategories =
        await categoryService.getCategoriesApiForDropdown(
          token,
          1,
          selectSearchTerm,
          authUserId
        );
      setCategories(fetchedCategories.data?.data.data);
    } catch (error) {
      console.error("Kategoriler alınırken bir hata oluştu:", error);
    }
  };

  // Kategoriler ilk yüklendiğinde ve arama terimi değiştiğinde
  useEffect(() => {
    fetchCategories();
  }, [selectSearchTerm]);

  // Kategori seçimi
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  // Aranacak kategori listesi
  const filteredCategories = categories?.filter
    ? categories.filter((category) =>
        category.name.toLowerCase().includes(selectSearchTerm.toLowerCase())
      )
    : [];

  // Text input
  const handleTextChange = (event) => {
    setText(event.target.value);
  };

  const handleReplaceChange = (event) => {
    setReplace(event.target.value);
  };

  const handleTypeChange = (event) => {
    const newType = event.target.value;
    setType(newType);
    // Üstüne Yaz seçildiğinde replace değerini temizle
    if (newType === 1) {
      setReplace("");
    }
  };

  const handleTextTypeChange = (event) => {
    setTextType(event.target.value);
  };

  // Başlık güncelleme
  const handleTextUpdate = async () => {
    if (text === "") {
      alert("Eski Metin boş geçilemez");
      return;
    }
    if (type === 0 && replace === "") {
      alert("Yeni Metin boş geçilemez");
      return;
    }
    if (selectedCategory === null || selectedCategory === "") {
      alert("Kategori Boş geçilemez");
      return;
    }

    let sendData = {
      id: 0,
      userId: authUserId,
      categoryId: selectedCategory.id,
      replace: replace,
      text: text,
      type: type,
      textType: textType, // 0: Başlık, 1: Açıklama
      state: 0,
      createDate: new Date().toISOString(),
      updateDate: new Date().toISOString(),
      ia: true,
    };

    try {
      await dispatch(addBulkText(sendData, token));
      showSuccessAlertBulkPrice();
      getBulkTextTransaction();
      // Form'u temizle
      setText("");
      setReplace("");
      setSelectedCategory(null);
      setTextType(0);
    } catch {
      showErrorAlertBulkPrice();
    }
  };


  // Toplu işlem kayıtlarını çek
  const getBulkTextTransaction = async () => {
    try {
      // API'ye page parametresi ile istekte bulunuyoruz
      const response = await dispatch(
        fetchBulkText(authUserId, token, page, pageSize)
      );
      console.log("BulkText Response:", response); // Debug için
      setTotalBulk(response.recordsTotal);
      setBulkTexts(response?.data);
    } catch (err) {
      console.log("Toplu işlemler getirilemedi", err);
    }
  };

  // İlk yüklemede toplu işlem kayıtlarını getir
  useEffect(() => {
    getBulkTextTransaction();
  }, []);

  // Sayfa her değiştiğinde toplu kayıtları tekrar getir
  useEffect(() => {
    getBulkTextTransaction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Durumlar
  const stateMap = {
    0: "Oluşturuldu",
    1: "Devam Ediyor",
    2: "Tamamlandı",
    3: "Yeniden Başlatıldı",
    9: "Başarısız",
  };

  // Metin kısaltma fonksiyonu
  const truncateText = (text, maxLength = 100) => {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Pagination için toplam sayfa sayısını hesaplıyoruz
  const totalPages = Math.ceil(totalBulk / pageSize);

  // MUI Pagination onChange handler
  const handleChangePage = (event, value) => {
    setPage(value);
  };

  return (
    <Box
      sx={{
        width: "100%",
        padding: { xs: 1, sm: 2, md: 3 },
        marginTop: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Box
        sx={{
          padding: { xs: 2, sm: 3, md: 4 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: { xs: 2, sm: 3, md: 4 },
          mb: { xs: 2, sm: 3 },
          ml: { xs: 0, sm: 1 },
          backgroundColor: "#F7F9FC",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: 4,
              height: { xs: 20, sm: 24 },
              backgroundColor: "#1F1B45",
              mr: 1,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontWeight: "bold",
              color: "#333",
              fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
            }}
          >
            Toplu Metin Güncelleme Yönetimi
          </Typography>
        </Box>
      </Box>
      <Alert
        sx={{ mb: 2, fontSize: { xs: "0.875rem", sm: "1rem" } }}
        severity="info"
        icon={<InfoIcon />}
      >
        <strong>Bilgi:</strong> Yapılan işlemlerin sisteme yansıması işlem yoğunluğuna göre 24 saati
        bulabilir. Toplu işlem oluşturmadan önce tüm alanların doğru doldurulduğundan emin olun.
      </Alert>
      
      {/* Bilgilendirme Kartı */}
      <Card
        sx={{
          mb: 3,
          backgroundColor: "#E3F2FD",
          borderLeft: "4px solid #2196F3",
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <InfoIcon sx={{ mr: 1, color: "#2196F3" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Nasıl Kullanılır?
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ ml: 4, color: "#424242" }}>
            <strong>Örnek Senaryo 1 (Değiştir):</strong> Ürün başlıklarında "premier kılıf" ifadesini "yumuşak kılıf" ile değiştirmek istiyorsunuz.
            <br />
            • <strong>Güncellenecek Alan:</strong> Başlık seçin
            <br />
            • <strong>Eski Metin:</strong> "premier kılıf" yazın
            <br />
            • <strong>Yeni Metin:</strong> "yumuşak kılıf" yazın (zorunlu)
            <br />
            • <strong>İşlem Tipi:</strong> Değiştir seçin
            <br />
            • Kategori seçerek işlemi başlatın
            <br />
            <br />
            <strong>Örnek Senaryo 2 (Üstüne Yaz):</strong> Ürün başlıklarında "premier kılıf" ifadesinin üzerine yeni metin eklemek istiyorsunuz.
            <br />
            • <strong>Güncellenecek Alan:</strong> Başlık seçin
            <br />
            • <strong>Eski Metin:</strong> "premier kılıf" yazın
            <br />
            • <strong>İşlem Tipi:</strong> Üstüne Yaz seçin
            <br />
            • Kategori seçerek işlemi başlatın
            <br />
            <br />
            <strong>Not:</strong> "Üstüne Yaz" işlem tipi seçildiğinde "Yeni Metin" alanı gösterilmez.
          </Typography>
        </CardContent>
      </Card>
      
      <Divider sx={{ marginBottom: 3 }} />

      {/* Kategori Seçimi */}
      <Card
        sx={{
          backgroundColor: "#fff",
          padding: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          boxShadow: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: "600",
            mb: 1.5,
            color: "#333",
            fontSize: { xs: "0.875rem", sm: "0.9375rem" },
          }}
        >
          Kategori Seçimi
        </Typography>
        <FormControl fullWidth size="small">
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option.name}
            value={selectedCategory}
            onChange={(event, newValue) => setSelectedCategory(newValue)}
            clearOnEscape
            renderInput={(params) => (
              <TextField
                {...params}
                label="Kategori"
                placeholder="Kategori seçiniz"
                size="small"
                required
                sx={{
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                }}
              />
            )}
          />
        </FormControl>
        <Typography variant="caption" sx={{ color: "#666", mt: 1, display: "block" }}>
          İşlem bu kategorideki ürünlere uygulanacaktır
        </Typography>
      </Card>

      {/* Metin İşlem Formu */}
      <Card
        sx={{
          backgroundColor: "#fff",
          padding: { xs: 2, sm: 3 },
          borderRadius: 2,
          boxShadow: 2,
          marginTop: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <SwapHorizIcon sx={{ mr: 1, color: "#1F1B45", fontSize: 28 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
            }}
          >
            Metin Güncelleme İşlemi
          </Typography>
        </Box>

        {/* Adım 1: Güncellenecek Alan ve İşlem Tipi */}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: "600",
            mb: 2,
            color: "#666",
            fontSize: { xs: "0.875rem", sm: "0.9375rem" },
          }}
        >
          Adım 1: İşlem Ayarları
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {textType === 0 ? <TitleIcon sx={{ mr: 0.5, fontSize: 18 }} /> : <DescriptionIcon sx={{ mr: 0.5, fontSize: 18 }} />}
                  Güncellenecek Alan
                </Box>
              </InputLabel>
              <Select
                value={textType}
                onChange={handleTextTypeChange}
                label="Güncellenecek Alan"
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                }}
              >
                <MenuItem value={0} sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TitleIcon sx={{ mr: 1, fontSize: 18 }} />
                    Başlık
                  </Box>
                </MenuItem>
                <MenuItem value={1} sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <DescriptionIcon sx={{ mr: 1, fontSize: 18 }} />
                    Açıklama
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>İşlem Tipi</InputLabel>
              <Select
                value={type}
                onChange={handleTypeChange}
                label="İşlem Tipi"
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                }}
              >
                <MenuItem value={0} sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                  Değiştir
                </MenuItem>
                <MenuItem value={1} sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                  Üstüne Yaz
                </MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" sx={{ color: "#666", mt: 0.5, display: "block" }}>
              {type === 0 
                ? "Eski metin tamamen yeni metinle değiştirilir" 
                : "Eski metin yeni metinle üzerine yazılır"}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Adım 2: Metinler */}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: "600",
            mb: 2,
            color: "#666",
            fontSize: { xs: "0.875rem", sm: "0.9375rem" },
          }}
        >
          Adım 2: Metin Bilgileri
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={type === 1 ? 12 : 6}>
            <TextField
              size="small"
              label="Eski Metin"
              variant="outlined"
              fullWidth
              required
              sx={{
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                },
                "& .MuiInputBase-input": {
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                },
              }}
              value={text}
              onChange={handleTextChange}
              placeholder="Örn: premier kılıf"
              helperText={textType === 0 ? "Ürün başlığında aranacak eski metin" : "Ürün açıklamasında aranacak eski metin"}
            />
          </Grid>
          {type === 0 && (
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label="Yeni Metin"
                variant="outlined"
                fullWidth
                required
                sx={{
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                }}
                value={replace}
                onChange={handleReplaceChange}
                placeholder="Örn: yumuşak kılıf"
                helperText="Eski metnin yerine gelecek yeni metin"
              />
            </Grid>
          )}
        </Grid>
        <Divider sx={{ my: 3 }} />
        
        <Button
          variant="contained"
          sx={{
            borderRadius: 2,
            width: "100%",
            backgroundColor: "#1F1B45",
            color: "#FFFFFF",
            textTransform: "none",
            fontWeight: 600,
            fontSize: { xs: "0.9375rem", sm: "1rem" },
            padding: { xs: "12px 24px", sm: "14px 28px" },
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "center",
            boxShadow: "0 4px 8px rgba(31, 27, 69, 0.3)",
            "&:hover": {
              backgroundColor: "#2C285A",
              boxShadow: "0 6px 12px rgba(31, 27, 69, 0.4)",
            },
          }}
          onClick={handleTextUpdate}
          disabled={!text || (type === 0 && !replace) || !selectedCategory}
        >
          <SwapHorizIcon sx={{ fontSize: 20 }} />
          Metin Güncelleme İşlemini Başlat
        </Button>
      </Card>

      {/* Toplu işlemler tablosu */}
      <Box
        sx={{
          backgroundColor: "#fff",
          padding: { xs: 2, sm: 3 },
          borderRadius: 2,
          boxShadow: 2,
          marginTop: 3,
          overflowX: "auto",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            marginBottom: 2,
            fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
          }}
        >
          Toplu Metin Güncelleme İşlemleri
        </Typography>
        <TableContainer>
          <Table size="small" aria-label="bulk text table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                  <strong>ID</strong>
                </TableCell>
                <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                  <strong>Kategori</strong>
                </TableCell>
                <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                  <strong>Eski Metin</strong>
                </TableCell>
                <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                  <strong>Yeni Metin</strong>
                </TableCell>
                <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                  <strong>Güncellenecek Alan</strong>
                </TableCell>
                <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                  <strong>İşlem Tipi</strong>
                </TableCell>
                <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                  <strong>Durum</strong>
                </TableCell>
                <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                  <strong>Oluşturma Tarihi</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bulkTexts && bulkTexts.length > 0 ? (
                bulkTexts.map((item) => (
                  <TableRow key={item?.id}>
                    <TableCell
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {item?.id}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {item?.category?.name || "-"}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      title={item?.text || "-"}
                    >
                      {truncateText(item?.text)}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      title={item?.replace || "-"}
                    >
                      {truncateText(item?.replace)}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {item?.textType === 0 ? "Başlık" : item?.textType === 1 ? "Açıklama" : "-"}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {item?.type === 0 ? "Değiştir" : "Üstüne Yaz"}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {stateMap[item?.state] || "Bilinmeyen"}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {item?.createDate
                        ? new Date(item.createDate).toLocaleDateString()
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Henüz işlem bulunmamaktadır.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination bileşeni */}
        {totalPages > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: 2,
              "& .MuiPaginationItem-root": {
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                minWidth: { xs: "32px", sm: "40px" },
                height: { xs: "32px", sm: "40px" },
              },
            }}
          >
            <Pagination
              color="primary"
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              size="medium"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BatchTextProcesses;
